const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const normalizeSourceSet = (values = []) => {
  return new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => normalizeText(value))
      .filter(Boolean)
  );
};

const normalizeDocIdSet = (values = []) => {
  return new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  );
};

const textContainsAny = (text = '', terms = []) => {
  const normalized = normalizeText(text);
  if (!normalized) return false;
  return (Array.isArray(terms) ? terms : [])
    .map((value) => normalizeText(value))
    .filter(Boolean)
    .some((term) => normalized.includes(term));
};

export const applyRouterGuidedAdjustments = ({
  candidates = [],
  routerContract = null,
  addRetrievalReason
} = {}) => {
  const rows = Array.isArray(candidates) ? candidates : [];
  if (!routerContract || typeof routerContract !== 'object') {
    return rows;
  }

  const preferSources = normalizeSourceSet(routerContract?.routing?.prefer_sources);
  const avoidSources = normalizeSourceSet(routerContract?.routing?.avoid_sources);
  const preferDocIds = normalizeDocIdSet(routerContract?.routing?.prefer_doc_ids);
  const avoidDocIds = normalizeDocIdSet(routerContract?.routing?.avoid_doc_ids);
  const positiveLensTerms = Array.isArray(routerContract?.semantic_lenses)
    ? routerContract.semantic_lenses
      .map((entry) => String(entry?.name || '').trim())
      .filter(Boolean)
    : [];
  const negativeTerms = Array.isArray(routerContract?.negative_filters)
    ? routerContract.negative_filters.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  const adjusted = rows.map((item) => {
    const source = normalizeText(item?.source || '');
    const docId = Number(item?.meta?.docId || 0);
    let delta = 0;

    if (source && preferSources.has(source)) {
      delta += 0.22;
    }
    if (source && avoidSources.has(source)) {
      delta -= 0.3;
    }
    if (docId > 0 && preferDocIds.has(docId)) {
      delta += 0.26;
    }
    if (docId > 0 && avoidDocIds.has(docId)) {
      delta -= 0.35;
    }

    const text = String(item?.text || '');
    if (positiveLensTerms.length > 0 && textContainsAny(text, positiveLensTerms)) {
      delta += 0.16;
    }
    if (negativeTerms.length > 0 && textContainsAny(text, negativeTerms)) {
      delta -= 0.28;
    }

    if (delta === 0) return item;

    const baseScore = toFiniteNumber(item?.baseScore, 0);
    return {
      ...item,
      baseScore: Math.max(0, baseScore + delta),
      meta: {
        ...(item?.meta && typeof item.meta === 'object' ? item.meta : {}),
        routerAdjusted: true,
        routerDelta: Number(delta.toFixed(3))
      }
    };
  });

  const appliedCount = adjusted.filter((item) => item?.meta?.routerAdjusted === true).length;
  if (appliedCount > 0 && typeof addRetrievalReason === 'function') {
    addRetrievalReason('router_adjustments_applied');
  }

  return adjusted;
};
